<!--
QuickAdd is for adding an item to a project/cart with a selected substrate and size.

- Logged in users get a project selector with their projects and workspaces.
- Anonymous users are prompted for their email address to create a project/cart, on first add.

- The substrate and size options come from an item's metadata when available,
  or from querying the availablesizes API with the item's aspect ratio.
- Selected options can be passed in, which will be mutated when the selections change,
  so selections can be preserved when the item changes.
- When allowed, custom sizes can be entered by typing in the size field.
  - Type both dimensions to specify a size e.g. 8x6
  - Type one dimension to generate dynamic options for proportionate sizes
    e.g. With a 4x3 image, typing 8 will show options for 8x6 and 10.67x8
  - Recent custom sizes are saved to user preferences for persistence across sessions.
    - Saved dynamic sizes will be adaptive to the aspect ratio of the current item.
-->

<template>
  <div>
    <div class="search-option">
      <div class="title">
        <label for="quick-add-substrate" class="m-0">Substrate</label>
      </div>
      <Multiselect
        id="quick-add-substrate"
        v-model="selected.substrate"
        track-by="value"
        label="label"
        placeholder="Select"
        :options="substrateOptions"
        :loading="substratesLoading"
        :show-labels="false"
        :preselect-first="false"
        :allow-empty="false"
        @input="onSelectSubstrate"
      ></Multiselect>
    </div>
    <div class="search-option">
      <div class="title">
        <label for="quick-add-size" class="m-0">Size in inches</label>
      </div>
      <Multiselect
        id="quick-add-size"
        v-model="selected.size"
        track-by="label"
        :placeholder="customSizesAllowed ? 'Select or type custom size (e.g. 20x30)' : 'Select size'"
        :options="sizeOptionsFiltered"
        :loading="sizesLoading"
        label="label"
        :preselect-first="false"
        :show-labels="false"
        :allow-empty="false"
        :taggable="customSizesAllowed"
        :tag-placeholder="customSizesAllowed ? 'Press enter to add custom size' : ''"
        :internal-search="false"
        @tag="addCustomSize"
        @search-change="onSizeSearchChange"
        @select="onSizeSelect"
      ></Multiselect>
    </div>
    <div class="search-option" v-show="profile">
      <div class="title">
        <label for="quick-add-workspace" class="m-0">Project</label>
      </div>
      <QuickAddWorkspace
        id="quick-add-workspace"
        ref="workspaceInput"
        v-model="selected.workspace"
        :profile="profile"
        :busy.sync="workspacesLoading"
      />
    </div>
    <div class="search-option d-flex gap-2" v-if="isStaff">
      <div>
        <div class="title">
          <label for="quick-add-quantity" class="m-0">Quantity</label>
        </div>
        <b-form-spinbutton
          id="quick-add-quantity"
          v-model="selected.quantity"
        />
      </div>
      <div class="flex-fill">
        <div class="title">
          <label for="quick-add-location" class="m-0">Location</label>
        </div>
        <b-form-input
          id="quick-add-location"
          v-model="selected.location"
          placeholder="Enter location"
          trim
        />
      </div>
    </div>
    <div class="mt-3">
      <LoadingButton
        variant="primary"
        class="mr-2 mb-2"
        @click="add(item)"
        :busy="busy === 'add'"
        :disabled="disabled"
      >Add {{profile ? 'to Project' : 'to Cart'}}</LoadingButton>
      <button
        type="button"
        class="btn btn-link mb-2"
        :disabled="disabled"
        @click="onCustomizeClick($event, item)"
      >Customize</button>
      <b-spinner small
        class="ml-2 align-baseline"
        v-show="!profile && workspacesLoading"
      />
    </div>
    <QuickModal
      id="modal-anon"
      title="Enter your email to save your cart"
      ref="modalAnon"
      centered
    >
      <AnonUserForm
        ref="formAnon"
        @submit="$refs && $refs.modalAnon.hide('ok')"
      ><span/></AnonUserForm>
      <div class="form-row mt-n3">
        <div class="col offset-lg-3 offset-sm-4 text-muted">
          <small>If you already have an account, <router-link :to="{name: 'accounts-login', query: {redirect: $route.fullPath}}">sign in</router-link> instead.</small>
        </div>
      </div>
    </QuickModal>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import { mapState, mapActions } from 'vuex'
import { uniqBy } from 'lodash'

import QuickAddWorkspace from '@/components/QuickAddWorkspace.vue'
import AnonUserForm from '@/components/AnonUserForm.vue'
import QuickModal from '@/components/QuickModal.vue'
import { API_URL, UI_URL } from '@/services/constants.js'
import { http, getAppApi } from '@/services/http.js'
import { getSrc } from '@/services/media.js'
import {
  createSizeOption,
  generateDynamicSizeOptions,
  getCustomSizesAllowed,
  getSizeOptions,
  getSubstrateOptions,
  loadSizingChoices,
  normalizeSizingSelection,
  parseDimensionString,
} from '@/utils/sizingPolicy.js'

export { createSizeOption, generateDynamicSizeOptions, parseDimensionString } from '@/utils/sizingPolicy.js'

export default {
  name: 'QuickAdd',
  components: {
    Multiselect,
    QuickAddWorkspace,
    AnonUserForm,
    QuickModal,
  },
  props: {
    item: Object,
    selected: {
      type: Object,
      default: () => ({
        substrate: null,
        size: null,
        workspace: null,
        quantity: 1,
        location: '',
      })
    },
  },
  data() {
    return {
      substrates: [
      ],
      sizes: {
      },
      substratesLoading: false,
      sizesLoading: false,
      workspacesLoading: false,
      busy: false,
      appApi: getAppApi(),
      // This component interacts with external state through this.selected and
      // this.$SmartProgress. this.foreground supports graceful interaction with that state.
      foreground: true,
      dynamicSizeOptions: [], // Dynamic size options based on typed numbers
      sizeSearchQuery: '', // Current search query for size filtering
    }
  },
  computed: {
    ...mapState('user', {
      user: 'user',
      profile: 'profile',
    }),
    isStaff() {
      return this.$store.getters['user/isStaff']
    },
    ds2() {
      return Boolean(this.user?.DS2)
    },
    'customSizes'() {
      return this.$store.state.preferences?.customSizes || []
    },
    cart: {
      get() {
        return this.$store.state.cart.cart
      },
      set(val) {
        this.$store.commit('cart/setCart', val)
      }
    },
    substrateOptions() {
      return getSubstrateOptions(this.substrates)
    },
    sizeOptions() {
      return getSizeOptions({
        item: this.item,
        substrate: this.selected.substrate,
        sizes: this.sizes,
        customSizes: this.customSizes,
        profile: this.profile,
        dynamicSizeOptions: this.dynamicSizeOptions,
      })
    },
    sizeOptionsFiltered() {
      const { normalized } = parseDimensionString(this.sizeSearchQuery)

      return this.sizeOptions.filter(size =>
        size.label?.toLowerCase().includes(normalized.toLowerCase())
      )
    },
    standardSizes() {
      return normalizeSizingSelection({
        item: this.item,
        substrate: this.selected.substrate,
        size: this.selected.size,
        substrates: this.substrates,
        sizes: this.sizes,
        customSizes: this.customSizes,
        profile: this.profile,
        dynamicSizeOptions: this.dynamicSizeOptions,
      }).standardSizes
    },
    customSizesAllowed() {
      return getCustomSizesAllowed({
        item: this.item,
        substrate: this.selected.substrate,
        profile: this.profile,
      })
    },
    designStudioQuery() {
      const query = {
        image_id: this.item?.id,
        substrate: this.selected?.substrate?.value,
        width: this.selected?.size?.value?.w,
        height: this.selected?.size?.value?.h,
        quantity: this.selected?.quantity,
        location: this.selected?.location,
        project_id: this.selected?.workspace?.project,
        workspace: this.selected?.workspace?.name,
      }

      return Object.fromEntries(
        Object.entries(query).filter(([, i]) => i)
      )
    },
    disabled() {
      return Boolean(this.busy)
          || !(this.selected.size && this.selected.substrate)
          || (!this.selected.workspace && this.workspacesLoading)
          || this.substratesLoading
          || this.sizesLoading
    },
  },
  watch: {
    item: {
      handler: 'onChange',
      immediate: true,
    }
  },
  created() {
    // component is no longer foreground if route change is initiated
    this.deregisterRouterGuard = this.$router.beforeEach((to, from, next) => {
      this.foreground = false
      next()
    })
  },
  destroyed() {
    this.foreground = false
    if (this.busy === 'open') {
      this.$SmartProgress.finish()
    }
    this.deregisterRouterGuard?.()
  },
  methods: {
    ...mapActions('user', [
      'fetchProfile',
    ]),
    getAnonEmail() {
      return this.$refs?.modalAnon.getValue(() => this.$refs?.formAnon.validate())
        .then(data => data.email)
    },
    async onChange(item) {
      this.$refs.workspaceInput && this.$refs.workspaceInput.onChange()
      await this.loadSizes(item)
      if (!this.foreground) { return } // don't interfere with other instances
      this.updateSelection()
    },
    updateSelection() {
      const selection = normalizeSizingSelection({
        item: this.item,
        substrate: this.selected.substrate,
        size: this.selected.size,
        substrates: this.substrates,
        sizes: this.sizes,
        customSizes: this.customSizes,
        profile: this.profile,
        dynamicSizeOptions: this.dynamicSizeOptions,
      })
      this.selected.substrate = selection.substrate
      this.selected.size = selection.size
    },
    onSelectSubstrate() {
      const selection = normalizeSizingSelection({
        item: this.item,
        substrate: this.selected.substrate,
        size: this.selected.size,
        substrates: this.substrates,
        sizes: this.sizes,
        customSizes: this.customSizes,
        profile: this.profile,
        dynamicSizeOptions: this.dynamicSizeOptions,
      })
      this.selected.size = selection.size
    },
    resetChoices() {
      this.substrates = []
      this.sizes = {}
      this.dynamicSizeOptions = []
    },
    async loadSizes(item, onUpdate) {
      if (!item) {
        this.resetChoices()
        return
      }
      const hasEmbeddedSubstrates = Array.isArray(item.substrates)
      const hasEmbeddedSizes = Boolean(item.substrate_sizes)
      this.substratesLoading = !hasEmbeddedSubstrates
      this.sizesLoading = !hasEmbeddedSizes

      return loadSizingChoices(item)
        .then(({ substrates, sizes }) => {
          this.substrates = substrates
          this.sizes = sizes
          onUpdate?.()
        })
        .catch(this.resetChoices)
        .finally(() => {
          this.substratesLoading = false
          this.sizesLoading = false
        })
    },
    async save(item, action, {onStart} = {}){
      const { workspace } = this.selected
      const legacyData = {
        imagedata: item,
        size: this.selected.size.value,
        substrate: this.selected.substrate.value,
        action: action,
        workspace_id: workspace?.id,
        quantity: this.selected.quantity,
        location: this.selected.location,
      }

      let token      = workspace?.project ?? this.cart?.token
      let project_id = workspace?.project ?? this.cart?.project?.id
      let url        = `${UI_URL}${this.ds2 ? 'projects2' : 'projects'}/${project_id}`

      // get email address from anon users
      let cart_email
      if (!token && !this.profile) {
        cart_email = await this.getAnonEmail()
      }

      // start progress UI
      if (onStart) { onStart() }

      try {
        // if no cart, create a new cart
        if (!token) {
          this.cart = await this.appApi.post('/api/v2/projects/', {cart_email})
            .then(response => response.data)
          if (!this.profile) {
            this.$store.commit('cart/setGuest', true)
          } else {
            // make cart owned by logged in user
            // TODO: set ownership automatically when creating cart, then remove this
            await this.appApi.post(`/api/v2/projects/${this.cart.token}/migrate-cart/`)
          }
          token      = this.cart?.token
          project_id = this.cart?.project?.id
          url        = `${UI_URL}${this.ds2 ? 'projects2' : 'projects'}/${project_id}`
          /* global dataLayer */
          dataLayer.push({
            'event': 'cart_create',
            'project_id': project_id,
          })
          /* global salesiqReady */
          salesiqReady.then((salesiq) => {
            salesiq.visitor.customaction('Cart create', {
              url,
              project_id,
            })
          }).catch(() => {})
        }

        // add item to cart
        const response = this.ds2
          ? await this.appApi.post(
            '/api/v3/design-studio/items/',
            this.designStudioItemPayload(item, action, project_id),
          )
          : await this.appApi.post(`/api/v2/projects/${token}/items/`, legacyData)
        const item_id = response.data.id
        dataLayer.push({
          'event': `cart_${action}`, // e.g. cart_add, cart_open
          'item_id': item_id,
          'project_id': project_id,
        })
        salesiqReady.then((salesiq) => {
          salesiq.visitor.customaction(`Cart ${action}`, {
            url,
            project_id,
            item_id,
          })
        }).catch(() => {})

        // report popular
        await http.patch(
          `/content/edit/${item.id}/`,
          {popular: true},
        ).catch(() => {})

        return {response, project_id, token}
      } catch (err) {
        const message = getErrorDetail(err) || err
        this.$alert.simple(`${message}`, {variant: 'danger'})
        throw err
      }
    },
    async add(item) {
      try {
        const {project_id, token} = await this.save(item, 'add', {
          onStart: () => this.busy = 'add',
        })
        await this.onAdded(item, project_id, token)
      } finally {
        this.busy = false
      }
    },
    designStudioItemPayload(item, action, projectId) {
      const substrate = this.selected.substrate.label || this.selected.substrate.value
      const width = this.selected.size.value.w
      const height = this.selected.size.value.h

      return {
        imagedata: item,
        substrate,
        action,
        project_id: projectId,
        workspace: this.selected.workspace?.name || '',
        quantity: Number(this.selected.quantity) || 1,
        location: this.selected.location || '',
        width,
        height,
        config_data: {
          substrate,
          width,
          height,
          chosen_options: [],
        },
      }
    },
    async onAdded(item, project_id, token) {
      if (this.ds2) {
        this.toast(item, `/projects2/${project_id}`)
        this.updateWorkspaces()
        return
      }
      const addedToActiveCart = !this.cart || this.cart?.project?.id === project_id
      if (addedToActiveCart) {
        this.toast(item, '/cart')
        // update cart if it is the current project
        // cart is not updated until async image processing completes, so wait a bit before refreshing
        setTimeout(async () => {
          await this.updateCart(token)
          this.updateWorkspaces()
        }, 3000)
      } else {
        this.toast(item, `/projects/${project_id}`)
      }
    },
    async updateCart(token) {
          this.cart = await this.appApi.get(`/api/v2/projects/${token}/`)
            .then(response => response.data)
          this.$store.dispatch('cart/fetchItems')
    },
    toast(item, to) {
      const image = this.$createElement('img', {attrs: {src: getSrc(item, 'thumb')}, class: 'toast-image mr-2'})
      const message = `"${item.title}" added to ${this.profile ? 'Project' : 'Cart'}`
      this.$bvToast.toast(
        [image, message],
        {to, variant: 'success', noCloseButton: true, solid: true, bodyClass: 'd-flex align-items-center text-reset'}
      )
    },
    async updateWorkspaces() {
      if (!this.selected.workspace && this.$refs.workspaceInput) {
        if (this.user) {
          await this.fetchProfile().catch(() => {})
        }
        this.$refs.workspaceInput.loadWorkspaces()
      }
    },
    async open(item) {
      const { response, project_id, token } = await this.save(item, 'open', {
        onStart: () => {
          this.busy = 'open'
          this.$SmartProgress.start()
        },
      })
        .catch(err => {
          this.busy = false
          this.$SmartProgress.fail()
          throw err
        })
      if (this.foreground) {
        window.location = `${API_URL}art/design/${response.data.id}`
      } else {
        // if a link was clicked during the request, it implies the user no
        // longer wants the pending navigation, so notify instead
        await this.onAdded(item, project_id, token)
        this.busy = false
      }
    },
    onCustomizeClick(event, item) {
      if (this.$store.state.user.user?.DS2) {
        this.$router.push({
          path: '/design-studio',
          query: this.designStudioQuery,
        })
        return
      }
      this.open(item)
    },
    addCustomSize(searchQuery) {
      if (!this.customSizesAllowed) {
        return
      }

      const { size } = parseDimensionString(searchQuery)
      if (!size.w && !size.h) {
        return
      }

      const selection = createSizeOption(size, this.item?.max_width / this.item?.max_height)
      if (!Object.values(selection.value).every(value => value >= 0.1 && value <= 1000)) {
        return
      }

      this.saveCustomSizeToPreferences(selection)
      this.selected.size = selection
    },
    onSizeSelect(selectedOption) {
      if (this.dynamicSizeOptions.includes(selectedOption)) {
        this.saveCustomSizeToPreferences(selectedOption)
      } else if (selectedOption.userInput) {
        this.saveCustomSizeToPreferences(selectedOption)
      }
    },
    onSizeSearchChange(searchQuery) {
      if (!this.customSizesAllowed) { return }
      this.dynamicSizeOptions = generateDynamicSizeOptions(searchQuery, this.item)
      this.sizeSearchQuery = searchQuery
    },
    saveCustomSizeToPreferences(customSize) {
      let customSizes = [customSize, ...this.customSizes];
      // Deduplicate before slicing so we don't lose any sizes due to reordering
      customSizes = uniqBy(customSizes, i => JSON.stringify(i.userInput));
      // Limit the number of custom sizes for usability, the rest of the code is designed to support an arbitrary number
      customSizes = customSizes.slice(0, 1);
      this.$store.commit('preferences/setPreference', { customSizes });
    },
  },
}

function getErrorDetail(err) {
  const message = err?.response?.data?.detail
    || JSON.stringify(err?.response?.data)
  return message && new Error(message)
}
</script>

<style lang="scss" scoped>
.search-option {
  text-align: left;
  & + & {
    margin-top: 0.25rem;
  }
  .title {
    font-size: 0.8em;
  }
}
</style>
